import {
    LINE_ITEM_TYPE_CARD,
    MEDIUM_TYPE_PHYSICAL,
} from '@/prisma/schemaConstants'
import { useBasicGiftCardContext } from '@/src/common/contexts/BasicGiftCardContext'
import { useCartContext } from '@/src/common/contexts/CartContext'
import {
    createCartObjectFromApiResponse,
    getDesignImage,
    handleCustomizationData,
} from '@/src/common/contexts/utilities'
import useGetCartManually from '@/src/common/hooks/useGetCartManually'
import usePostAddCart from '@/src/common/hooks/usePostAddCart'
import { recipientPlasticReducer } from '@/src/common/reducers/plasticRecipientReducer'
import checkKYCMinMaxLimits from '@/src/common/utilities/checkKYCMinMaxLimits'
import getDefaultDenom from '@/src/common/utilities/getDefaultDenom'
import getDefaultPredesign from '@/src/common/utilities/getDefaultPredesign'
import { VisaBACVisited } from '@/src/common/utilities/gtag'
import {
    CARRIER_FONT_HANDWRITTEN,
    CARRIER_TYPE_GREETING_CARD,
    CARRIER_TYPE_STANDARD,
    SET_GIFT_STATE,
    SET_RECIPIENT,
} from '@/src/modules/build-a-card/constants'
import {
    TDelivery,
    TLineItemForBAC,
    TRecipientPlasticInfo,
    TRecipientPlasticState,
} from '@/src/modules/build-a-card/inputs/types'
import { TKYCMinMaxValidations } from '@/src/modules/build-a-card/types'
import updateExistingPlasticInDelivery from '@/src/modules/build-a-card/utilities/updateExistingPlasticInDelivery'
import { ICart } from '@/src/modules/cart/ICart'
import { KYC_MAX_CARDS, minCards } from '@/src/modules/checkout/strings'
import round from 'lodash/round'
import { useRouter } from 'next/router'
import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useReducer,
    useState,
} from 'react'

import { QueryObserverResult } from 'react-query'
import { v4 as uuid } from 'uuid'
import sendDataLayerEvents from '@/src/modules/build-a-card/utilities/sendDataLayerEvents'
import determineNextRouteAndPush from '@/src/modules/build-a-card/utilities/determineNextRouteAndPush'

// this context handles the non-box plastic purchase flow

type TPlasticGiftQuantityState = {
    setDisplayLiveErrors: Function
    isLoading: boolean
    setIsLoading: Function
    continueIsLoading: boolean
    setContinueIsLoading: Function
    validateAndAddCart: Function
    KYCMinMaxLimits: TKYCMinMaxValidations
    displayLiveErrors: boolean
    giftState: TRecipientPlasticState
    dispatch: Function
    showHandwrittenModal: boolean
    setShowHandwrittenModal: Function
    cardQuantity: number
}

export const PlasticGiftQuantityContext =
    createContext<TPlasticGiftQuantityState | null>(null)

export const usePlasticGiftQuantityContext = (): TPlasticGiftQuantityState => {
    const context = useContext(PlasticGiftQuantityContext)

    if (!context) {
        throw new Error(
            'Please use PlasticGiftQuantityContext in parent component'
        )
    }

    return context
}

interface IGiftQuantity {
    children: ReactNode
    productUuid: string
}

export const PlasticGiftQuantityProvider = ({
    children,
    productUuid,
}: IGiftQuantity) => {
    const { refetch } = useGetCartManually()
    const { mutate: addCartMutate } = usePostAddCart()
    const {
        hasGreetingCard,
        greetingCardUuid,
        nameOnCard,
        messageOnCard,
        carrierType,
        fontSelection,
        setNameOnCard,
        denomination,
        setDenomination,
        setCarrierType,
        setGreetingCardUuid,
        setDesignName,
        setPredesignUuid,
        setDesignImage,
        setCustomDesignObject,
        setFontSelection,
        setMessageOnCard,
        predesignUuid,
        customDesignObject,
        designName,
        predesigns,
        defaultToPredesign,
    } = useBasicGiftCardContext()

    const router = useRouter()
    const [cardQuantity, setCardQuantity] = useState<number>(1)
    const [listOfRecipients, setListOfRecipients] = useState<
        TRecipientPlasticInfo[]
    >([
        {
            recipientName: '',
            recipientMessage: '',
            senderName: '',
            recipientHasError: false,
        },
    ])
    // this is used for KYC limit tracking
    const [itemDenomFromCart, setItemDenomFromCart] = useState<number>(0)
    const [analyticWasSent, setAnalyticWasSent] = useState<boolean>(false)
    const [displayLiveErrors, setDisplayLiveErrors] = useState<boolean>(false)
    const [showHandwrittenModal, setShowHandwrittenModal] = useState(true)

    const [editingLineItemUuid, setEditingLineItemUuid] = useState<
        string | null
    >(null)
    const [editingDeliveryUuid, setEditingDeliveryUuid] = useState<
        string | null
    >(null)

    const [giftState, dispatch] = useReducer(recipientPlasticReducer, {
        isEditing: !!router?.query?.item_edit,
        listOfRecipients: listOfRecipients,
        setListOfRecipients: setListOfRecipients,
        cardQuantity: cardQuantity,
        setCardQuantity: setCardQuantity,
        minValue: minCards,
        maxValue: KYC_MAX_CARDS,
        isHandwritten: fontSelection === CARRIER_FONT_HANDWRITTEN,
        hasGreetingCard: hasGreetingCard,
    })

    const { isEditing } = giftState
    const { cartTotalLoad, cartTotalCards } = useCartContext()

    const KYCMinMaxLimits = checkKYCMinMaxLimits({
        isEditing: isEditing,
        totalValueLoadInCart: cartTotalLoad,
        totalNumberOfCardsInCart: cartTotalCards,
        itemDenomFromCart: itemDenomFromCart,
        newGiftTotalDenom: round(parseFloat(denomination) * cardQuantity, 2),
        newCardQuantity: cardQuantity,
    })

    // used for the add to cart buttons
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [continueIsLoading, setContinueIsLoading] = useState<boolean>(false)

    useEffect(() => {
        getDefaultPredesign({
            predesigns: predesigns,
            setPredesignUuid: setPredesignUuid,
            setDesignImage: setDesignImage,
            setDesignName: setDesignName,
            defaultToPredesign: defaultToPredesign,
        })
        setDenomination(getDefaultDenom(50))
    }, [
        defaultToPredesign,
        predesigns,
        setDenomination,
        setDesignImage,
        setDesignName,
        setPredesignUuid,
    ])

    // push datalayer event
    useEffect(() => {
        if (designName && !analyticWasSent) {
            VisaBACVisited(
                ['eec.detail', 'view_item'],
                MEDIUM_TYPE_PHYSICAL,
                designName,
                denomination,
                cardQuantity,
                false
            )
            setAnalyticWasSent(true)
        }
    }, [analyticWasSent, denomination, designName])

    useEffect(() => {
        dispatch({
            type: SET_GIFT_STATE,
            payload: {
                listOfRecipients: listOfRecipients,
                cardQuantity: cardQuantity,
                isEditing: !!router?.query?.item_edit,
                isHandwritten: fontSelection === CARRIER_FONT_HANDWRITTEN,
                hasGreetingCard: hasGreetingCard,
            },
        })
    }, [
        cardQuantity,
        fontSelection,
        hasGreetingCard,
        listOfRecipients,
        router?.query?.item_edit,
    ])

    // This effect loads a LineItem for editing, if applicable.
    useEffect(() => {
        const lineItemUuidToEdit = router.query.item_edit

        // If there is no query param with the line item UUID, return.
        if (!lineItemUuidToEdit) return

        // Do a fresh fetch of the cart
        refetch().then(async (response) => {
            const cartData = { ...(response.data as ICart) }

            // Find the Delivery that contains the LineItem.
            const delivery = cartData.cart_contents?.deliveries?.find(
                (delivery) =>
                    delivery.line_items?.find(
                        (li) => li.line_item_uuid === lineItemUuidToEdit
                    )
            )

            let lineItem
            if (delivery) {
                const { delivery_uuid } = delivery
                setEditingDeliveryUuid(delivery_uuid)
                lineItem = delivery.line_items?.find(
                    (li) => li.line_item_uuid === lineItemUuidToEdit
                )
            }

            if (lineItem) {
                // update the default empty recipient with the editing details
                dispatch({
                    type: SET_RECIPIENT,
                    payload: {
                        recipientName:
                            lineItem?.customization_data?.carrier_to ?? '',
                        recipientMessage:
                            lineItem?.customization_data?.carrier_message ?? '',
                        senderName:
                            lineItem?.customization_data?.carrier_from ?? '',
                        recipientIndex: 0,
                    },
                })

                setEditingLineItemUuid(lineItem.line_item_uuid)
                setDenomination(lineItem.denomination)
                setItemDenomFromCart(
                    round(parseFloat(lineItem.denomination), 2)
                )
                setCarrierType(
                    lineItem?.greeting_card?.resolved_data
                        ? CARRIER_TYPE_GREETING_CARD
                        : CARRIER_TYPE_STANDARD
                )

                if (lineItem?.greeting_card?.greeting_card_uuid) {
                    const { greeting_card_uuid } = lineItem.greeting_card
                    setGreetingCardUuid(greeting_card_uuid)
                }
                handleCustomizationData(
                    setNameOnCard,
                    setMessageOnCard,
                    setFontSelection,
                    setShowHandwrittenModal,
                    lineItem
                )
                getDesignImage(
                    lineItem,
                    setPredesignUuid,
                    setDesignImage,
                    setDesignName,
                    setCustomDesignObject
                )
            }
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refetch, router.query.item_edit])

    const assembleLineItems: Function = useCallback(() => {
        const lineItems: TLineItemForBAC[] = []
        listOfRecipients.forEach((recipient) => {
            const isHandwritten =
                hasGreetingCard && fontSelection === CARRIER_FONT_HANDWRITTEN
            lineItems.push({
                line_item_uuid: editingLineItemUuid ?? uuid(),
                line_item_type: LINE_ITEM_TYPE_CARD,
                denomination: denomination,
                item_price: denomination,
                product: {
                    product_uuid: productUuid,
                },
                design: {
                    design_uuid: predesignUuid,
                },
                custom_design: customDesignObject,
                greeting_card: {
                    greeting_card_uuid: hasGreetingCard ? greetingCardUuid : '',
                },
                customization_data: {
                    name_on_card: nameOnCard,
                    message_on_card: messageOnCard,
                    carrier_from: isHandwritten
                        ? undefined
                        : recipient.senderName,
                    carrier_to: isHandwritten
                        ? undefined
                        : recipient.recipientName,
                    carrier_message: recipient.recipientMessage,
                    is_handwritten: isHandwritten,
                },
            })
        })
        return lineItems
    }, [
        customDesignObject,
        denomination,
        editingLineItemUuid,
        fontSelection,
        greetingCardUuid,
        hasGreetingCard,
        listOfRecipients,
        messageOnCard,
        nameOnCard,
        predesignUuid,
        productUuid,
    ])

    const assembleDelivery = (lineItems: TLineItemForBAC[]) => {
        const newDelivery: TDelivery = {
            delivery_uuid: editingDeliveryUuid ?? uuid(),
            delivery_option: {
                delivery_option_uuid: '',
            },
            delivery_details: {
                sender_name: '',
                recipient_name: '',
                recipient_message: '',
            },
            line_items: lineItems,
        }
        return newDelivery
    }

    const addCart = async ({
        keepShopping = false,
    }: {
        keepShopping: boolean
    }) => {
        // Fetch the latest cart data from the RQ hook... then post a new cart with this new line_item.
        await refetch().then(async (response: QueryObserverResult) => {
            const cartData = createCartObjectFromApiResponse(response)
            const lineItems: any = assembleLineItems()
            const deliveries = cartData.cart_contents.deliveries ?? []

            // check if there's an existing delivery plastics
            const existingPlasticDeliveryIndex: number = deliveries.findIndex(
                (delivery) =>
                    delivery.line_items.find((item) => {
                        return (
                            item?.product?.resolved_data?.product_medium
                                .medium_type === MEDIUM_TYPE_PHYSICAL
                        )
                    })
            )

            // check if there are already multiple deliveries with only plastics
            const isMultiDelivery: boolean =
                deliveries.filter((delivery) =>
                    delivery.line_items.every((lineItem) => {
                        return (
                            lineItem.product?.resolved_data?.product_medium
                                .medium_type === MEDIUM_TYPE_PHYSICAL
                        )
                    })
                ).length > 1

            // if they're editing, update the existing line item
            if (editingLineItemUuid) {
                updateExistingPlasticInDelivery({
                    editingLineItemUuid: editingLineItemUuid,
                    existingPlasticDeliveryIndex: existingPlasticDeliveryIndex,
                    lineItem: lineItems[0],
                    cartData: cartData,
                })
            } else if (isMultiDelivery) {
                // add new delivery
                cartData.cart_contents.deliveries.push(
                    // @ts-ignore
                    assembleDelivery(lineItems)
                )
            } else if (existingPlasticDeliveryIndex >= 0) {
                // if there is an existing delivery, add the items to it
                cartData.cart_contents.deliveries[
                    existingPlasticDeliveryIndex
                ].line_items.push(...lineItems)
            } else {
                // if not, add the delivery to the cart
                const newDelivery = assembleDelivery(lineItems)
                // @ts-ignore
                cartData.cart_contents.deliveries.push(newDelivery)
            }

            addCartMutate(cartData, {
                onSuccess: async () => {
                    sendDataLayerEvents({
                        brandName: 'Visa',
                        cardQuantity: cardQuantity,
                        designName: designName,
                        denomination: Number(denomination),
                        productMediumType: MEDIUM_TYPE_PHYSICAL,
                    })

                    if (keepShopping) {
                        await determineNextRouteAndPush({ isEditing, router })
                    } else {
                        await router.push(`/checkout`)
                    }
                },
                onError: () => {
                    alert('Unable to add item to cart. Please try again.')
                },
            })
        })
    }

    const validateAndAddCart = useCallback(
        ({ keepShopping = false }: { keepShopping: boolean }) => {
            setDisplayLiveErrors(true)
            // check if all recipient info has been added to the list successfully
            const totalRecipientMatch: boolean =
                listOfRecipients.length === cardQuantity
            const formIsValid: boolean =
                listOfRecipients.filter((recipient) => {
                    return (
                        recipient.recipientHasError === true ||
                        !recipient.recipientMessage ||
                        (carrierType === CARRIER_TYPE_STANDARD &&
                            (!recipient.recipientName || !recipient.senderName))
                    )
                }).length === 0

            if (!totalRecipientMatch || !formIsValid) {
                setIsLoading(false)
                setContinueIsLoading(false)
                return
            }

            addCart({
                keepShopping: keepShopping,
            }).then(() => {})
        },
        [addCart, cardQuantity, listOfRecipients]
    )

    const functions = useMemo(
        () => ({
            setDisplayLiveErrors,
            setIsLoading,
            setContinueIsLoading,
            setShowHandwrittenModal,
        }),
        []
    )

    const values = useMemo(
        () => ({
            isLoading,
            continueIsLoading,
            giftState,
            dispatch,
            validateAndAddCart,
            displayLiveErrors,
            showHandwrittenModal,
            cardQuantity,
            KYCMinMaxLimits,
        }),
        [
            continueIsLoading,
            giftState,
            isLoading,
            validateAndAddCart,
            displayLiveErrors,
            showHandwrittenModal,
            cardQuantity,
            KYCMinMaxLimits,
        ]
    )

    const finalValues = useMemo(
        () => ({ ...values, ...functions }),
        [functions, values]
    )

    return (
        <PlasticGiftQuantityContext.Provider value={finalValues}>
            {children}
        </PlasticGiftQuantityContext.Provider>
    )
}
